import React, { createElement } from 'react';
import { XIcon, PackageIcon, AlertTriangleIcon, DownloadIcon } from 'lucide-react';
interface StockReferenceItem {
  id: string;
  reference: string;
  design: string;
  desiredStock: number;
  currentStock: number;
  incoming: number;
  difference: number;
  percentage: number;
  priority: 'critical' | 'high' | 'medium';
}
interface StockListModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'overstock' | 'lowstock';
  items: StockReferenceItem[];
}
export function StockListModal({
  isOpen,
  onClose,
  type,
  items
}: StockListModalProps) {
  if (!isOpen) return null;
  const isOverStock = type === 'overstock';
  const title = isOverStock ? 'Over Stock References' : 'Low Stock References';
  const headerBgColor = isOverStock ? 'from-blue-500 to-blue-600' : 'from-yellow-500 to-yellow-600';
  const handleDownloadExcel = () => {
    const headers = ['Reference', 'Design', 'Desired Stock', 'Current Stock', 'Incoming', isOverStock ? 'Surplus' : 'Shortage', 'Stock %', 'Priority'];
    const rows = items.map(item => [item.reference, item.design, item.desiredStock.toString(), item.currentStock.toString(), item.incoming.toString(), Math.abs(item.difference).toString(), `${item.percentage}%`, item.priority.toUpperCase()]);
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}-references-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };
  const totalDifference = items.reduce((sum, item) => sum + Math.abs(item.difference), 0);
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-id="element-467">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col" data-id="element-468">
        <div className={`bg-gradient-to-r ${headerBgColor} text-white p-6 flex items-center justify-between`} data-id="element-469">
          <div className="flex items-center gap-4" data-id="element-470">
            <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center" data-id="element-471">
              {isOverStock ? <PackageIcon className="w-6 h-6" data-id="element-472" /> : <AlertTriangleIcon className="w-6 h-6" data-id="element-473" />}
            </div>
            <div data-id="element-474">
              <h2 className="text-2xl font-bold" data-id="element-475">{title}</h2>
              <p className={`${isOverStock ? 'text-blue-100' : 'text-yellow-100'} text-sm mt-1`} data-id="element-476">
                {items.length} references need attention
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/20 transition-colors" data-id="element-477">
            <XIcon className="w-6 h-6" data-id="element-478" />
          </button>
        </div>
        <div className="p-6 border-b border-gray-200 bg-gray-50" data-id="element-479">
          <div className="flex items-center justify-between" data-id="element-480">
            <div className="flex items-center gap-6" data-id="element-481">
              <div className="text-center" data-id="element-482">
                <p className="text-sm text-gray-600 mb-1" data-id="element-483">
                  Total {isOverStock ? 'Surplus' : 'Shortage'}
                </p>
                <p className={`text-3xl font-bold ${isOverStock ? 'text-blue-600' : 'text-yellow-600'}`} data-id="element-484">
                  {totalDifference}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-485">units</p>
              </div>
              <div className="w-px h-12 bg-gray-300" data-id="element-486"></div>
              <div className="text-center" data-id="element-487">
                <p className="text-sm text-gray-600 mb-1" data-id="element-488">Critical Items</p>
                <p className="text-3xl font-bold text-gray-900" data-id="element-489">
                  {items.filter(i => i.priority === 'critical').length}
                </p>
              </div>
            </div>
            <button onClick={handleDownloadExcel} className="flex items-center gap-2 px-5 py-2.5 bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors shadow-sm" data-id="element-490">
              <DownloadIcon className="w-4 h-4" data-id="element-491" />
              Download Excel
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6" data-id="element-492">
          <div className="space-y-3" data-id="element-493">
            {items.map(item => <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-all" data-id="element-494">
                <div className="flex items-start justify-between mb-4" data-id="element-495">
                  <div className="flex items-start gap-4 flex-1" data-id="element-496">
                    <div className={`w-12 h-12 bg-gradient-to-br ${isOverStock ? 'from-blue-500 to-blue-600 shadow-blue-500/30' : 'from-yellow-500 to-yellow-600 shadow-yellow-500/30'} rounded-lg flex items-center justify-center shadow-lg`} data-id="element-497">
                      <PackageIcon className="w-6 h-6 text-white" data-id="element-498" />
                    </div>
                    <div className="flex-1" data-id="element-499">
                      <div className="flex items-center gap-3 mb-2" data-id="element-500">
                        <h3 className="text-lg font-bold text-gray-900" data-id="element-501">
                          {item.reference}
                        </h3>
                        <span className={`px-2.5 py-1 rounded text-xs font-semibold border ${getPriorityColor(item.priority)}`} data-id="element-502">
                          {item.priority.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3" data-id="element-503">
                        {item.design}
                      </p>
                      <div className="grid grid-cols-5 gap-4" data-id="element-504">
                        <div data-id="element-505">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-506">Desired</p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-507">
                            {item.desiredStock}
                          </p>
                        </div>
                        <div data-id="element-508">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-509">Current</p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-510">
                            {item.currentStock}
                          </p>
                        </div>
                        <div data-id="element-511">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-512">Incoming</p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-513">
                            {item.incoming}
                          </p>
                        </div>
                        <div data-id="element-514">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-515">
                            {isOverStock ? 'Surplus' : 'Shortage'}
                          </p>
                          <p className={`text-lg font-bold ${isOverStock ? 'text-blue-600' : 'text-yellow-600'}`} data-id="element-516">
                            {Math.abs(item.difference)}
                          </p>
                        </div>
                        <div data-id="element-517">
                          <p className="text-xs text-gray-500 mb-1" data-id="element-518">Stock %</p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-519">
                            {item.percentage}%
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-4 border-t border-gray-100" data-id="element-520">
                  <button className="flex-1 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded transition-colors border border-blue-200 text-sm font-medium" data-id="element-521">
                    View Details
                  </button>
                  <button className={`px-4 py-2 bg-gradient-to-r ${isOverStock ? 'from-blue-500 to-blue-600' : 'from-yellow-500 to-yellow-600'} text-white rounded hover:opacity-90 transition-all text-sm font-medium shadow-sm`} data-id="element-522">
                    {isOverStock ? 'Manage Stock' : 'Add to Order'}
                  </button>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}